import { useCallback, useMemo } from 'react';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { useGetCurrentUser } from '@/api/generated';

const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';
const CURRENT_USER_FORMAT_PREFERENCES_STALE_TIME_MS = 15 * 60 * 1000;
const DATE_ONLY_FORMATS: Record<string, string> = {
  'DD/MM/YYYY': 'dd/MM/yyyy',
  'MM/DD/YYYY': 'MM/dd/yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd',
  'YYYY/MM/DD': 'yyyy/MM/dd'
};
const DATE_TIME_FORMATS: Record<string, string> = {
  'DD/MM/YYYY': 'dd/MM/yyyy HH:mm',
  'MM/DD/YYYY': 'MM/dd/yyyy HH:mm',
  'YYYY-MM-DD': 'yyyy-MM-dd HH:mm',
  'YYYY/MM/DD': 'yyyy/MM/dd HH:mm'
};

function getDateFnsFormat(userFormat: string | undefined, map: Record<string, string>): string {
  const key = userFormat && map[userFormat] ? userFormat : DEFAULT_DATE_FORMAT;

  return map[key];
}

function toDate(value: string | Date): Date {
  if (value instanceof Date) return value;
  const s = value.trim();

  return parseISO(s);
}

export function useFormattedDatetime() {
  const { data: user } = useGetCurrentUser({
    query: {
      // Date-format consumers often render many times in tables; keep user preferences warm
      // so each cell mount does not immediately make `/me` eligible for refetching.
      staleTime: CURRENT_USER_FORMAT_PREFERENCES_STALE_TIME_MS
    }
  });

  const timezone = user?.preferredTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dateFormat = user?.preferredDateFormat ?? DEFAULT_DATE_FORMAT;

  const dateOnlyFormat = useMemo(() => getDateFnsFormat(dateFormat, DATE_ONLY_FORMATS), [dateFormat]);
  const dateTimeFormat = useMemo(() => getDateFnsFormat(dateFormat, DATE_TIME_FORMATS), [dateFormat]);

  const formatDate = useCallback(
    (value: string | Date | null | undefined): string => {
      if (value == null) return '';
      try {
        const date = toDate(value as string | Date);
        if (Number.isNaN(date.getTime())) return '';

        return formatInTimeZone(date, timezone, dateOnlyFormat);
      } catch {
        return '';
      }
    },
    [timezone, dateOnlyFormat]
  );

  const formatDatetime = useCallback(
    (value: string | Date | null | undefined): string => {
      if (value == null) return '';
      try {
        const date = toDate(value as string | Date);
        if (Number.isNaN(date.getTime())) return '';

        return formatInTimeZone(date, timezone, dateTimeFormat);
      } catch {
        return '';
      }
    },
    [timezone, dateTimeFormat]
  );

  return { formatDate, formatDatetime };
}
